import axios from 'axios'
import store from './store'
import router from './router'

// JWT debugging utilities
function parseJwt(token) {
  try {
    if (!token) return null
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))
    return JSON.parse(jsonPayload)
  } catch (e) {
    return null
  }
}

function isTokenExpired(token) {
  const payload = parseJwt(token)
  if (!payload || !payload.exp) return true
  const currentTime = Math.floor(Date.now() / 1000)
  return payload.exp < currentTime
}

function logTokenInfo(token, label = 'Token') {
  if (!token) {
    return
  }
}

let host = window.location.hostname
let port = host == 'localhost' ? 8000 : null
let protocol = window.location.protocol

let SERVER = port == null ? `${protocol}//${host}` : `${protocol}//${host}:${port}`

let config = {
  baseURL: `${SERVER}/api`,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
}

const httpClient = axios.create(config)

// Request queue for handling multiple failed requests during token refresh
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

/**
 * Handles all errors - triggers notification
 * @param {Object} error 
 */
function catchError(error) {
  let message = null

  if (error.response) {
    if (error.response.status) {
      // Suppress both 401 and 403 unauthorized errors
      if (error.response.status != 401 && error.response.status != 403) {
        if (error.response.status == 400) {
          // Handle new custom exception handler format
          if (error.response.data && error.response.data.error) {
            message = error.response.data.error
            // If there are additional details, append them
            if (error.response.data.details && typeof error.response.data.details === 'string') {
              message += ': ' + error.response.data.details
            } else if (error.response.data.details && typeof error.response.data.details === 'object') {
              // Handle validation error details
              const detailMessages = []
              Object.keys(error.response.data.details).forEach(field => {
                const fieldErrors = error.response.data.details[field]
                if (Array.isArray(fieldErrors)) {
                  detailMessages.push(`${field}: ${fieldErrors.join(', ')}`)
                } else {
                  detailMessages.push(`${field}: ${fieldErrors}`)
                }
              })
              if (detailMessages.length > 0) {
                message += ' - ' + detailMessages.join('; ')
              }
            }
          }
          // Fallback to old DRF format for backward compatibility
          else if ('non_field_errors' in error.response.data) {
            message = error.response.data.non_field_errors[0]
          } else if ('detail' in error.response.data) {
            message = error.response.data.detail.replace('"','')
          } else {
            message = `Fields required: ${Object.keys(error.response.data).join(', ')}`
          }
        } else if (error.response.status == 500) {
          // Handle new custom exception handler format
          if (error.response.data && error.response.data.error) {
            message = error.response.data.error
          }
          // Fallback to old format
          else if (typeof error.response.data === 'object' && error.response.data !== null) {
            if ('detail' in error.response.data) {
              message = error.response.data.detail.replace('"','')
            } else {
              message = error.response.statusText
            } 
          } else {
            message = error.response.statusText
          }
        } else {
          // Handle other status codes - check for new format first
          if (error.response.data && error.response.data.error) {
            message = error.response.data.error
          } else {
            message = error.response.statusText
          }
        }
      } else {
        // Handle 401 and 403 errors - suppress alerts but log for debugging
        message = null // Always suppress 401/403 alerts
      }
    } else {
      message = error.response
    }
  } else {
    message = error.message
  }
  
  // Additional check: don't show error message if this is a 401 that will be retried
  if (error.response && error.response.status === 401 && error.config && !error.config.__isRetryRequest) {
    message = null
  }
  
  if (message != null) {
    store.commit('showSnackbar', { text: message, color: 'error' })
  }
}
// 
// Intercept requests inject authorization header
// 
httpClient.interceptors.request.use(
  config => {
    let token = store.state.access
    if (token) {
      // Debug token status before making request
      if (isTokenExpired(token)) {
        logTokenInfo(token, 'Expired Access Token')
      }
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  }, 
  error => { 
    return Promise.reject(error) 
  }
)

// 
// Custom response interceptor with proper request queuing
// 
httpClient.interceptors.response.use(
  response => { 
    return response
  },
  async error => {
    const originalRequest = error.config

    // Check if this is a 401 error and we haven't already tried to refresh
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Token refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          return httpClient(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      logTokenInfo(store.state.access, 'Current Access Token')
      logTokenInfo(store.state.refresh, 'Current Refresh Token')

      if (!store.state.refresh) {
        isRefreshing = false
        processQueue(new Error('No refresh token'), null)
        store.commit('authLogout')
        router.push({ name: 'login' })
        return Promise.reject(error)
      }

      if (isTokenExpired(store.state.refresh)) {
        isRefreshing = false
        processQueue(new Error('Refresh token expired'), null)
        store.commit('authLogout')
        router.push({ name: 'login' })
        return Promise.reject(error)
      }

      try {
        const tokenRefreshResponse = await httpClient.post('/token/refresh/', { 
          refresh: store.state.refresh 
        }, {
          skipAuthRefresh: true,  // Prevent infinite loop
          _retry: true  // Mark as retry to avoid recursion
        })

        const newAccessToken = tokenRefreshResponse.data.access
        
        logTokenInfo(newAccessToken, 'New Access Token')
        if (tokenRefreshResponse.data.refresh) {
          logTokenInfo(tokenRefreshResponse.data.refresh, 'New Refresh Token')
        }
        
        // Update tokens in store
        store.commit('authRefresh', newAccessToken)
        if (tokenRefreshResponse.data.refresh) {
          store.commit('setRefreshToken', tokenRefreshResponse.data.refresh)
        }
        
        // Update the current request's authorization header
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
        
        // Process the queue of failed requests
        processQueue(null, newAccessToken)
        
        isRefreshing = false
        
        return httpClient(originalRequest)
        
      } catch (refreshError) {
        processQueue(refreshError, null)
        isRefreshing = false
        
        store.commit('authLogout')
        router.push({ name: 'login' })
        return Promise.reject(refreshError)
      }
    }

    catchError(error)
    store.state.busy = false
    return Promise.reject(error)
  }
)

export default httpClient
